import {
  createAudioEngine,
  createSilentAudioEngine,
  type AudioEngine,
  type RenderWorkerLike,
} from '@hf/audio';
import { audioTier, loadAudioManifest } from '@hf/client-core/audio-bridge/manifest';

export {
  AUDIO_MANIFEST_PATH,
  audioTier,
  loadAudioManifest,
  type AudioTier,
} from '@hf/client-core/audio-bridge/manifest';

const baseUrl = (): string => {
  try {
    return import.meta.env?.BASE_URL ?? '/';
  } catch {
    return '/';
  }
};

/**
 * The SFX bank renders in a Worker (it used to take ~300 ms of main thread in idle slices: a match started within
 * a second or two of boot built its touchdown fanfare on the frame of the first touchdown). Null where Workers are
 * missing: the engine then renders in idle slices.
 */
const createSfxWorker = (): RenderWorkerLike | null => {
  if (typeof Worker !== 'function') return null;
  try {
    return new Worker(new URL('./sfx-render.worker.ts', import.meta.url), {
      type: 'module',
      name: 'hf-sfx-render',
    }) as unknown as RenderWorkerLike;
  } catch {
    return null;
  }
};

/** Wait for an idle moment (at least `minMs`, at most ~`minMs + 1.5 s`). */
const idle = (minMs: number): Promise<void> =>
  new Promise((resolve) => {
    const g = globalThis as { requestIdleCallback?: (cb: () => void, o?: { timeout: number }) => unknown };
    setTimeout(() => {
      if (typeof g.requestIdleCallback === 'function')
        g.requestIdleCallback(() => resolve(), { timeout: 1500 });
      else resolve();
    }, minMs);
  });

/**
 * THE audio engine factory used by src/app: the WebAudio engine from @hf/audio (silent where WebAudio does not
 * exist: Node tests, very old browsers). The AudioContext is created ahead of the first gesture (suspended until
 * it resumes it: constructing one costs ~150-200 ms of main thread, which must not land on the PRESS START click),
 * but only once the title has painted and the main thread is idle (`eagerContext: 'idle'`: created synchronously
 * in boot it delayed the title's first paint by that much). public/audio/audio-manifest.json is registered at once (captions and durations known) and its files load
 * in tiers (`audioTier`), decoded off the frame path. Assets the app's asset manifest lists (`manifest.audio`) can
 * be added later with `engine.load(...)`; loading is idempotent per id.
 */
export const createClientAudioEngine = (): AudioEngine => {
  const g = globalThis as { AudioContext?: unknown; webkitAudioContext?: unknown; window?: unknown };
  if (!g.window || (!g.AudioContext && !g.webkitAudioContext)) return createSilentAudioEngine();
  const base = baseUrl();
  const engine = createAudioEngine({ baseUrl: base, eagerContext: 'idle', renderWorker: createSfxWorker });
  void loadAudioManifest(undefined, base).then(async (assets) => {
    await engine.load(assets, { defer: true });
    await engine.prefetch?.((a) => audioTier(a) === 0);
    await idle(900);
    await engine.prefetch?.((a) => audioTier(a) <= 1);
    await idle(400);
    await engine.prefetch?.();
  });
  // Debug/e2e: the engine (stats(), onAnnounce/onLineEnd) on `window.__HF_AUDIO__` (read-only use).
  (g as { __HF_AUDIO__?: AudioEngine }).__HF_AUDIO__ = engine;
  return engine;
};
